
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreation,
  WebsiteDeletion,
  WebsiteOrchestratorResourceType,
} from '../../../services/Website/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import DomainDnsHover from '@/components/website/DomainDnsHover.vue';
import ServerDnsHover from '@/components/website/ServerDnsHover.vue';
import SslCertificateHover from '@/components/website/SslCertificateHover.vue';
import WebsiteErrorStatusHover from '@/components/website/WebsiteErrorStatusHover.vue';
import OwnerLabel from '@/components/website/OwnerLabel.vue';
import DeletionOrchestratorStatus from '@/components/website/DeletionOrchestratorStatus.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import { sleep } from '@/utils/websiteQuickCreate';

interface WebsiteSearchHistoryEntry {
  query: string;
  domains: string[];
  servers: string[];
  count: number;
  at: number;
}

type BulkDeleteStatus = 'queued' | 'deleting' | 'done' | 'failed';

interface BulkDeleteItem {
  id: number;
  domain: string;
  status: BulkDeleteStatus;
  error: string;
  deletion: WebsiteDeletion | null;
  orchLoading: boolean;
}

interface OrchestratorRowState {
  loading: boolean;
  creation: WebsiteCreation | null;
  deletion: WebsiteDeletion | null;
}

const SEARCH_HISTORY_KEY = 'websites.searchHistory';
const SEARCH_HISTORY_LIMIT = 10;
const BULK_DELETE_DELAY_MS = 500;
const ORCHESTRATOR_DELAY_DEFAULT_MS = 100;
const ORCHESTRATOR_DELAY_OPTIONS_MS: number[] = [50, 100, 200, 500, 750, 1000];

@Component({
  components: {
    DomainDnsHover,
    ServerDnsHover,
    SslCertificateHover,
    WebsiteErrorStatusHover,
    OwnerLabel,
    DeletionOrchestratorStatus,
    PageHeader,
    StatusChip,
  },
  inject: [
    'websiteRepository',
    'serverRepository',
  ],
})
export default class AllWebsites extends Vue {
  public loading: boolean = true;

  private websiteRepository!: WebsiteRepositoryInterface;

  private serverRepository!: ServerRepositoryInterface;

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 25,
  };

  public totalWebsitesCount = 10;

  public baseHeaders: DataTableHeader[] = [
    { text: 'Id', value: 'id', width: '80px' },
    { text: 'Domain', value: 'domain' },
    { text: 'Type', value: 'type', width: '100px' },
    { text: 'Server', value: 'originServer' },
    { text: 'Owner', value: 'userId' },
    { text: '', value: 'resources', sortable: false, width: '108px', align: 'center' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Created', value: 'createdAt', width: '150px' },
  ];

  public get headers(): DataTableHeader[] {
    if (!this.checkOrchestrator) {
      return this.baseHeaders;
    }

    const headers = this.baseHeaders.slice();
    headers.splice(headers.length - 2, 0, {
      text: 'Orchestrator',
      value: 'orchestrator',
      sortable: false,
      width: '280px',
    });
    return headers;
  }

  public get tableRenderKey(): string {
    return `websites-${this.checkOrchestrator ? 'orch' : 'plain'}`;
  }

  public websiteStatuses: string[] = [
    'PENDING',
    'DISABLED',
    'ACTIVE',
    'ERROR',
  ];

  public websiteSearch: string = '';
  public websites: Website[] = [];
  public searchHistory: WebsiteSearchHistoryEntry[] = [];
  public historyOpen: boolean = false;

  public serversMap: { [id: string]: string } = {};

  public selected: Website[] = [];
  public deleting: boolean = false;
  public deleteFinished: boolean = false;
  public deleteConfirmOpen: boolean = false;
  public deleteStopRequested: boolean = false;
  public deleteQueue: BulkDeleteItem[] = [];
  public activeDeleteId: number | null = null;
  private preserveDeleteProgress: boolean = false;

  public checkOrchestrator: boolean = false;
  public orchestratorDelayMs: number = ORCHESTRATOR_DELAY_DEFAULT_MS;
  public readonly orchestratorDelayOptions = ORCHESTRATOR_DELAY_OPTIONS_MS.map((ms) => ({
    text: `${ms} ms`,
    value: ms,
  }));
  public orchestratorMap: { [websiteId: number]: OrchestratorRowState } = {};
  private ignoreRouteSync: boolean = false;
  private applyingRouteQuery: boolean = false;
  private orchestratorLoadToken: number = 0;

  public get selectedPreview(): Website[] {
    return this.selected.slice(0, 8);
  }

  public get deleteCompletedCount(): number {
    return this.deleteQueue.filter((item) => item.status === 'done' || item.status === 'failed').length;
  }

  public get deleteProgressPercent(): number {
    if (!this.deleteQueue.length) {
      return 0;
    }
    return Math.round((this.deleteCompletedCount / this.deleteQueue.length) * 100);
  }

  public get deleteProgressLabel(): string {
    if (this.deleting && this.activeDeleteId !== null) {
      const current = this.deleteQueue.find((item) => item.id === this.activeDeleteId);
      if (current) {
        return `Deleting ${current.domain}`;
      }
    }
    if (this.deleteStopRequested && this.deleting) {
      return 'Stopping after current request…';
    }
    if (this.deleteFinished) {
      const failed = this.deleteQueue.filter((item) => item.status === 'failed').length;
      const queued = this.deleteQueue.filter((item) => item.status === 'queued').length;
      if (queued) {
        return 'Delete stopped — remaining sites still selected';
      }
      return failed
        ? `Delete finished with ${failed} error${failed === 1 ? '' : 's'}`
        : 'Delete finished successfully';
    }
    return 'Deleting websites…';
  }

  public get isFilteringHistory(): boolean {
    return Boolean(this.websiteSearch && this.websiteSearch.trim());
  }

  public get canCheckOrchestrator(): boolean {
    return (this.websiteSearch || '').trim().length > 3;
  }

  public get filteredSearchHistory(): WebsiteSearchHistoryEntry[] {
    const needle = (this.websiteSearch || '').trim().toLowerCase();
    if (!needle) {
      return this.searchHistory;
    }

    return this.searchHistory.filter((entry) => {
      if (entry.query.toLowerCase().indexOf(needle) !== -1) {
        return true;
      }

      if (entry.domains.some((domain) => domain.toLowerCase().indexOf(needle) !== -1)) {
        return true;
      }

      return entry.servers.some((server) => server.toLowerCase().indexOf(needle) !== -1);
    });
  }

  public async created(): Promise<void> {
    this.searchHistory = this.readSearchHistory();
    this.applyRouteQuery();
    this.loading = true;
    try {
      await this.loadWebsites(Boolean((this.websiteSearch || '').trim()));
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const query = this.$route.query;

      if (typeof query.q === 'string') {
        this.websiteSearch = query.q;
      } else {
        this.websiteSearch = '';
      }

      const page = Number(query.page);
      this.options.page = page > 0 ? page : 1;

      const pageSize = Number(query.pageSize);
      if (pageSize > 0) {
        this.options.itemsPerPage = pageSize;
      }

      // Orchestrator checks are manual only — never restore from the URL.
      // Drop legacy orch / orchDelay params so history/direct links do not auto-run.
      const stripOrchQuery = query.orch != null || query.orchDelay != null;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
        if (stripOrchQuery) {
          this.syncRouteQuery();
        }
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    const query: { [key: string]: string } = {};
    const search = (this.websiteSearch || '').trim();

    if (search) {
      query.q = search;
    }
    if (this.options.page > 1) {
      query.page = String(this.options.page);
    }
    if (this.options.itemsPerPage > 0 && this.options.itemsPerPage !== 25) {
      query.pageSize = String(this.options.itemsPerPage);
    }

    return query;
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    const next = this.buildListQuery();
    const keys = ['q', 'page', 'pageSize'];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const left = typeof query[key] === 'string' ? query[key] : '';
      const right = next[key] || '';
      if (left !== right) {
        return false;
      }
    }
    // Legacy orch params are no longer part of list state — treat as unequal so they get stripped.
    if (query.orch != null || query.orchDelay != null) {
      return false;
    }
    return true;
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'website_all', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public mounted(): void {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  public beforeDestroy(): void {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  public onDocumentMouseDown(event: MouseEvent): void {
    const wrap = this.$refs.searchHistoryWrap as HTMLElement | undefined;
    if (!wrap || !this.historyOpen) {
      return;
    }

    if (event.target instanceof Node && !wrap.contains(event.target)) {
      this.closeSearchHistory();
    }
  }

  public openSearchHistory(): void {
    if (this.searchHistory.length) {
      this.historyOpen = true;
    }
  }

  public closeSearchHistory(): void {
    this.historyOpen = false;
  }

  public async runSearch(): Promise<void> {
    this.closeSearchHistory();
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites(true);
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public async loadWebsites(recordHistory: boolean = false): Promise<void> {
    const shouldRecord = recordHistory === true;

    let domainSearch: string | undefined = this.websiteSearch || undefined;
    let statusSearch: string | undefined;

    if (this.websiteSearch && this.websiteStatuses.includes(this.websiteSearch.toUpperCase())) {
      domainSearch = undefined;
      statusSearch = this.websiteSearch;
    }

    const response = await this.websiteRepository.searchWebsites(
      this.options.page,
      domainSearch,
      undefined,
      undefined,
      statusSearch,
      this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 25,
    );
    const serversIds = response.Websites.map((website: Website) => website.originServer);

    const newServers: string[] = [];
    serversIds.forEach((serverId: string) => {
      if (this.serversMap[serverId]) {
        return;
      }

      newServers.push(serverId);
    });

    if (newServers.length > 0) {
      const servers = await this.serverRepository.getServersByIds(newServers);
      servers.forEach((server: ServerInterface) => {
        this.serversMap[server._id] = server.hostname;
      });
    }

    this.websites = response.Websites;
    this.totalWebsitesCount = response.count;
    if (!this.deleting) {
      this.selected = [];
      if (!this.preserveDeleteProgress) {
        this.deleteFinished = false;
        this.deleteQueue = [];
        this.activeDeleteId = null;
      }
    }

    if (shouldRecord) {
      this.recordSearchHistory(this.websiteSearch, response.Websites, response.count);
    }

    if (this.checkOrchestrator && this.canCheckOrchestrator) {
      await this.loadOrchestratorForWebsites(this.websites);
    } else {
      this.orchestratorLoadToken += 1;
      this.orchestratorMap = {};
    }
  }

  public orchLoading(websiteId: number): boolean {
    const row = this.orchestratorMap[websiteId];
    return Boolean(row && row.loading);
  }

  public orchCreation(websiteId: number): WebsiteCreation | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.creation ? row.creation : null;
  }

  public orchDeletion(websiteId: number): WebsiteDeletion | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.deletion ? row.deletion : null;
  }

  public async loadOrchestratorForWebsites(websites: Website[]): Promise<void> {
    const loadToken = this.orchestratorLoadToken + 1;
    this.orchestratorLoadToken = loadToken;

    const nextMap: { [websiteId: number]: OrchestratorRowState } = {};
    websites.forEach((website) => {
      nextMap[website.id] = { loading: true, creation: null, deletion: null };
    });
    this.orchestratorMap = { ...nextMap };

    for (let i = 0; i < websites.length; i += 1) {
      if (this.orchestratorLoadToken !== loadToken) {
        return;
      }

      const website = websites[i];
      try {
        const resources = await this.fetchLatestOrchestrator(website.id, false);
        if (this.orchestratorLoadToken !== loadToken) {
          return;
        }
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: resources.creation,
          deletion: resources.deletion,
        });
      } catch (err) {
        if (this.orchestratorLoadToken !== loadToken) {
          return;
        }
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: null,
          deletion: null,
        });
      }

      if (i < websites.length - 1) {
        await sleep(this.orchestratorDelayMs || ORCHESTRATOR_DELAY_DEFAULT_MS);
      }
    }
  }

  public async fetchLatestOrchestrator(
    websiteId: number,
    retry: boolean = true,
  ): Promise<{ creation: WebsiteCreation | null; deletion: WebsiteDeletion | null }> {
    const attempts = retry ? 3 : 1;
    let creation: WebsiteCreation | null = null;
    let deletion: WebsiteDeletion | null = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const resources = await this.websiteRepository.getWebsiteOrchestratorResources(websiteId);
      const creations = resources.creations || [];
      const deletions = resources.deletions || [];

      if (creations.length) {
        const latestCreation = creations.reduce((best, current) => (
          current.id > best.id ? current : best
        ));
        creation = await this.hydrateCreation(websiteId, latestCreation);
      }

      if (deletions.length) {
        const latestDeletion = deletions.reduce((best, current) => (
          current.id > best.id ? current : best
        ));
        deletion = await this.hydrateDeletion(websiteId, latestDeletion);
      }

      if (creation || deletion || attempt >= attempts - 1) {
        break;
      }

      await sleep(400);
    }

    return { creation, deletion };
  }

  public async fetchLatestDeletion(
    websiteId: number,
    retry: boolean = true,
  ): Promise<WebsiteDeletion | null> {
    const resources = await this.fetchLatestOrchestrator(websiteId, retry);
    return resources.deletion;
  }

  private async hydrateCreation(
    websiteId: number,
    creation: WebsiteCreation,
  ): Promise<WebsiteCreation> {
    if (creation.actions && creation.actions.length) {
      return creation;
    }

    const detail = await this.websiteRepository.getWebsiteOrchestratorResource(
      websiteId,
      creation.id,
      WebsiteOrchestratorResourceType.CREATION,
    );
    return detail.creation || creation;
  }

  private async hydrateDeletion(
    websiteId: number,
    deletion: WebsiteDeletion,
  ): Promise<WebsiteDeletion> {
    if (deletion.actions && deletion.actions.length) {
      return deletion;
    }

    const detail = await this.websiteRepository.getWebsiteOrchestratorResource(
      websiteId,
      deletion.id,
      WebsiteOrchestratorResourceType.DELETION,
    );
    return detail.deletion || deletion;
  }

  @Watch('checkOrchestrator')
  public async onCheckOrchestratorChange(): Promise<void> {
    if (this.deleting || this.applyingRouteQuery) {
      return;
    }
    await this.syncRouteQuery();
    if (this.loading) {
      return;
    }
    if (this.checkOrchestrator && this.canCheckOrchestrator && this.websites.length) {
      await this.loadOrchestratorForWebsites(this.websites);
    } else if (!this.checkOrchestrator) {
      this.orchestratorLoadToken += 1;
      this.orchestratorMap = {};
    }
  }

  @Watch('orchestratorDelayMs')
  public async onOrchestratorDelayChange(): Promise<void> {
    if (this.deleting || this.applyingRouteQuery || this.loading) {
      return;
    }
    if (!this.checkOrchestrator || !this.canCheckOrchestrator) {
      return;
    }
    await this.syncRouteQuery();
  }

  @Watch('websiteSearch')
  public watchSearch(): void {
    if (this.applyingRouteQuery) {
      return;
    }
    this.options.page = 1;
    if (this.searchHistory.length) {
      this.historyOpen = true;
    }
    if (!this.canCheckOrchestrator && this.checkOrchestrator) {
      this.checkOrchestrator = false;
      this.orchestratorLoadToken += 1;
      this.orchestratorMap = {};
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync || this.deleting) {
      return;
    }
    if (this.$route.name !== 'website_all') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    this.loading = true;
    try {
      await this.loadWebsites(Boolean((this.websiteSearch || '').trim()));
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public historySummary(entry: WebsiteSearchHistoryEntry): string {
    const count = typeof entry.count === 'number' ? entry.count : 0;
    const resultsLabel = `${count} result${count === 1 ? '' : 's'}`;
    const domains = entry.domains.length
      ? entry.domains.join(', ')
      : '';
    const servers = entry.servers.length
      ? entry.servers.join(', ')
      : '';

    const parts = [resultsLabel];
    if (domains) {
      parts.push(domains);
    }
    if (servers) {
      parts.push(servers);
    }
    return parts.join(' · ');
  }

  public historyTimeAgo(at: number): string {
    const ts = typeof at === 'number' ? at : Date.now();
    const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));

    if (seconds < 10) {
      return 'just now';
    }
    if (seconds < 60) {
      return `${seconds}s ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days}d ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months}mo ago`;
    }

    const years = Math.floor(days / 365);
    return `${years}y ago`;
  }

  public async applySearchHistory(entry: WebsiteSearchHistoryEntry): Promise<void> {
    this.websiteSearch = entry.query;
    await this.runSearch();
  }

  public typeColor(type: string): string {
    switch ((type || '').toUpperCase()) {
      case 'MAIN':
        return 'primary';
      case 'STAGING':
        return 'orange';
      case 'ALIAS':
        return 'teal';
      default:
        return 'grey';
    }
  }

  public formatCreatedAt(value: string): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }

  public databaseCount(website: Website): number {
    if (!website.details || !website.details.databaseIds) {
      return 0;
    }

    return website.details.databaseIds.length;
  }

  public hasSsl(website: Website): boolean {
    return Boolean(website.details && website.details.sslCertificateId);
  }

  public onSslDeleted(websiteId: number): void {
    const website = this.websites.find((item) => item.id === websiteId);
    if (!website || !website.details) {
      return;
    }
    this.$set(website.details, 'sslCertificateId', null);
  }

  public hasVhost(website: Website): boolean {
    return Boolean(website.details && website.details.vhostId);
  }

  public hasUnixUser(website: Website): boolean {
    return Boolean(website.details && website.details.unixUserId);
  }

  public hasDatabases(website: Website): boolean {
    return this.databaseCount(website) > 0;
  }

  private recordSearchHistory(query: string, websites: Website[], totalCount: number): void {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      return;
    }

    const domains: string[] = [];
    const servers: string[] = [];

    websites.forEach((website) => {
      if (website.domain && domains.indexOf(website.domain) === -1 && domains.length < 3) {
        domains.push(website.domain);
      }

      const serverName = this.serversMap[website.originServer] || website.originServer;
      if (serverName && servers.indexOf(serverName) === -1 && servers.length < 2) {
        servers.push(serverName);
      }
    });

    const needle = trimmed.toLowerCase();
    const next: WebsiteSearchHistoryEntry[] = [
      {
        query: trimmed,
        domains,
        servers,
        count: typeof totalCount === 'number' ? totalCount : websites.length,
        at: Date.now(),
      },
      ...this.searchHistory.filter((entry) => entry.query.toLowerCase() !== needle),
    ].slice(0, SEARCH_HISTORY_LIMIT);

    this.searchHistory = next;
    this.writeSearchHistory(next);
  }

  private readSearchHistory(): WebsiteSearchHistoryEntry[] {
    try {
      const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      const byQuery: { [query: string]: WebsiteSearchHistoryEntry } = {};
      parsed.forEach((entry) => {
        if (!entry || typeof entry.query !== 'string') {
          return;
        }

        const key = entry.query.trim().toLowerCase();
        if (!key) {
          return;
        }

        const at = typeof entry.at === 'number' ? entry.at : 0;
        const existing = byQuery[key];
        if (existing && existing.at >= at) {
          return;
        }

        byQuery[key] = {
          query: entry.query.trim(),
          domains: Array.isArray(entry.domains) ? entry.domains.slice(0, 3) : [],
          servers: Array.isArray(entry.servers) ? entry.servers.slice(0, 2) : [],
          count: typeof entry.count === 'number' ? entry.count : 0,
          at: at || Date.now(),
        };
      });

      return Object.keys(byQuery)
        .map((key) => byQuery[key])
        .sort((a, b) => b.at - a.at)
        .slice(0, SEARCH_HISTORY_LIMIT);
    } catch (err) {
      return [];
    }
  }

  private writeSearchHistory(entries: WebsiteSearchHistoryEntry[]): void {
    try {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(entries));
    } catch (err) {
      // Ignore quota / private-mode failures.
    }
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('An issue occurred while retrieving websites.');
    } finally {
      this.loading = false;
    }
  }

  public createWebsite(): void {
    this.$router.push({ name: 'website_create' });
  }

  public quickCreateWebsites(): void {
    this.$router.push({ name: 'website_quick_create' });
  }

  public rowClass(item: Website): string {
    const entry = this.deleteQueue.find((row) => row.id === item.id);
    if (!entry) {
      return '';
    }
    if (entry.status === 'deleting') {
      return 'website-row--deleting';
    }
    if (entry.status === 'failed') {
      return 'website-row--delete-failed';
    }
    if (entry.status === 'done') {
      return 'website-row--deleted';
    }
    return '';
  }

  public openDeleteConfirm(): void {
    if (!this.selected.length || this.deleting) {
      return;
    }
    this.deleteConfirmOpen = true;
  }

  public stopDelete(): void {
    this.deleteStopRequested = true;
  }

  public async confirmBulkDelete(): Promise<void> {
    if (!this.selected.length || this.deleting) {
      return;
    }

    this.deleteConfirmOpen = false;
    this.deleteFinished = false;
    this.deleteStopRequested = false;
    this.deleteQueue = this.selected.map((site) => ({
      id: site.id,
      domain: site.domain,
      status: 'queued' as BulkDeleteStatus,
      error: '',
      deletion: null,
      orchLoading: false,
    }));
    this.deleting = true;
    this.activeDeleteId = null;

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < this.deleteQueue.length; i += 1) {
      if (this.deleteStopRequested) {
        break;
      }

      const item = this.deleteQueue[i];
      this.activeDeleteId = item.id;
      this.$set(this.deleteQueue, i, {
        ...item,
        status: 'deleting',
        error: '',
        orchLoading: false,
      });

      try {
        await this.websiteRepository.deleteWebsite(item.id);
        this.$set(this.deleteQueue, i, {
          ...this.deleteQueue[i],
          status: 'done',
          error: '',
          orchLoading: true,
        });
        successCount += 1;
        this.websites = this.websites.filter((site) => site.id !== item.id);
        this.selected = this.selected.filter((site) => site.id !== item.id);
        this.totalWebsitesCount = Math.max(0, this.totalWebsitesCount - 1);

        try {
          const deletion = await this.fetchLatestDeletion(item.id, true);
          this.$set(this.deleteQueue, i, {
            ...this.deleteQueue[i],
            deletion,
            orchLoading: false,
          });
        } catch (orchErr) {
          this.$set(this.deleteQueue, i, {
            ...this.deleteQueue[i],
            orchLoading: false,
          });
        }
      } catch (err) {
        const anyErr = err as any;
        const message = anyErr.response?.data?.message || anyErr.message || 'Delete failed';
        this.$set(this.deleteQueue, i, {
          ...this.deleteQueue[i],
          status: 'failed',
          error: message,
          orchLoading: false,
        });
        failedCount += 1;
      }

      if (i < this.deleteQueue.length - 1 && !this.deleteStopRequested) {
        await sleep(BULK_DELETE_DELAY_MS);
      }
    }

    this.deleting = false;
    this.deleteFinished = true;
    this.activeDeleteId = null;
    this.deleteStopRequested = false;

    if (failedCount === 0 && successCount > 0) {
      this.$successDialog.show(
        successCount === 1
          ? 'Website deleted'
          : `${successCount} websites deleted`,
      );
    } else if (failedCount > 0) {
      this.$errorDialog.show(
        `${failedCount} delete${failedCount === 1 ? '' : 's'} failed`
        + (successCount ? `, ${successCount} succeeded` : ''),
      );
    }

    try {
      this.preserveDeleteProgress = true;
      await this.loadWebsites();
    } catch (err) {
      // Keep local state if refresh fails.
    } finally {
      this.preserveDeleteProgress = false;
    }
  }
}
